import { useField } from 'formik';
import { DropdownModel } from '../models/dropdown.model';
import { useCallback, useEffect, useRef, useState } from 'react';
import downArrow from '../assets/icons/downArrow.svg';

type IProps = {
	label: string;
	name: string;
	placeholder?: string;
	list: DropdownModel[];
	isRequired?: boolean;
	disabled?: boolean;
	labelClassName?: string;
	dropdownContainer?: string;
	selectedItemClassName?: string;
	listClassName?: string;
	listItemClassName?: string;
	onChangeSelection?: (val: DropdownModel) => void;
	customListItemComponent?: (val: DropdownModel) => JSX.Element;
};
export const Dropdown = ({
	label,
	name,
	placeholder,
	list,
	isRequired = true,
	disabled,
	labelClassName,
	dropdownContainer,
	selectedItemClassName,
	listClassName,
	listItemClassName,
	customListItemComponent,
	onChangeSelection,
	...props
}: IProps) => {
	const [field, meta, helpers] = useField(name);
	const ref = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const [selectedItem, setSelectedItem] = useState<string | null>(null);
	const [showList, setShowList] = useState(false);
	const [position, setPosition] = useState({
		top: 'top-[45px]',
		bottom: 'bottom-auto',
	});

	useEffect(() => {
		if (
			!selectedItem &&
			field?.value &&
			field?.value != '00000000-0000-0000-0000-000000000000'
		) {
			setSelectedItem(field?.value);
		} else if (field?.value === null || field?.value === '') {
			setSelectedItem(null);
		}
	}, [field, selectedItem]);

	const handleSelected = (item: DropdownModel) => {
		setSelectedItem(item.id);
		helpers.setValue(item.id);
		onChangeSelection?.(item);
		setShowList(!showList);
	};

	const handleClick = () => {
		setShowList(!showList);
	};

	const displayList = () => {
		return showList && !disabled ? (
			<div
				ref={listRef}
				className={` max-h-[300px] overflow-auto absolute w-full left-0 z-50 border-2 border-rcrm-primary-border-blue bg-white rounded-lg
				 ${position.top || ''} ${position.bottom || ''} ${listClassName ? listClassName : ''}`}
			>
				{list?.map((item, i) => (
					<div
						key={item.id}
						className={`border-b-2 px-3 w-full h-12 font-medium py-2 text-sm border-rcrm-primary-border-blue
						 ${list.length - 1 === i ? 'border-b-0' : ''} ${listItemClassName ? listItemClassName : ''}`}
						onClick={() => handleSelected(item)}
					>
						{customListItemComponent
							? customListItemComponent?.(item)
							: item.value}
					</div>
				))}
			</div>
		) : null;
	};

	/*The type of event is any cause it might change dynamically.*/
	const handleClickOutside = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(event: any) => {
			if (ref.current && !ref.current.contains(event.target)) {
				if (showList) {
					setShowList(false);
					if (!selectedItem) helpers.setTouched(true);
				}
			}
		},
		[helpers, selectedItem, ref, showList],
	);

	const handleScroll = () => {
		if (
			ref.current?.getBoundingClientRect()?.y &&
			ref.current?.getBoundingClientRect()?.x &&
			listRef.current?.getBoundingClientRect()?.height &&
			ref.current.getBoundingClientRect()?.width
		) {
			const refYPosition = ref.current?.getBoundingClientRect()?.y;
			const refHeight = ref.current?.getBoundingClientRect()?.height;
			const listHeight =
				refYPosition +
					refHeight +
					listRef.current?.getBoundingClientRect()?.height >
				window.innerHeight;
			if (listHeight) {
				setPosition({
					bottom: 'bottom-full',
					top: '',
				});
			} else {
				setPosition({
					bottom: ``,
					top: `top-full`,
				});
			}
		}
	};

	const getSelectedItem = () => {
		const val = list.find((e) => e.id === selectedItem);
		if (customListItemComponent && val) {
			return customListItemComponent(val);
		}
		return val?.value;
	};

	useEffect(() => {
		window.addEventListener('click', handleClickOutside);
		return () => {
			window.removeEventListener('click', handleClickOutside);
		};
	}, [handleClickOutside, showList]);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll, { capture: true });
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [setPosition, ref, listRef]);

	return (
		<label
			className={`flex flex-col ${!disabled ? 'cursor-pointer' : ''} ${labelClassName ?? ''}`}
			onClick={() => handleClick()}
		>
			<p
				className={`font-semibold text-base text-rcrm-title overflow-hidden whitespace-nowrap text-ellipsis`}
			>
				{label}
				{isRequired ? '*' : ''}
			</p>
			<div
				ref={ref}
				className={`relative flex justify-between border-2  p-3 rounded-lg
				 ${disabled ? 'bg-rcrm-disabled' : 'bg-white'}
				  ${meta.touched && meta.error ? 'border-rcrm-error' : 'border-rcrm-primary-border-blue'} ${dropdownContainer}`}
				onClick={() => handleClick()}
			>
				<p
					className={`text-sm font-medium  overflow-hidden whitespace-nowrap text-ellipsis ${selectedItemClassName ? selectedItemClassName : ''}`}
				>
					{disabled && !selectedItem
						? 'NA'
						: selectedItem
							? getSelectedItem()
							: placeholder}
				</p>
				{!disabled ? (
					<div className="flex justify-center items-center">
						<img
							src={downArrow}
							className="w-3 h-3"
							alt="downarrow"
						/>
					</div>
				) : null}
				{displayList()}
			</div>
			{meta.touched && meta.error ? (
				<div className="text-xs text-rcrm-error">{meta.error}</div>
			) : null}
		</label>
	);
};

export default Dropdown;
